import PatientsCardView from "@/components/card/PatientsCardView";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DataTable, DataTableOptions, DataTableRef } from "@/components/tables";
import { DataGrid, DataGridOptions, DataGridRef } from "@/components/grids";
import { useAccessCheck } from "@/hooks/useAccess";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Tab,
  TabList,
  Tabs,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { Filter, LayoutGrid, List, Plus, Search } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PatientOverview } from "@/types";
import AddPatientModal from "@/components/modals/CreatePatientModal";
import apiClient from "@/lib/api/client";

const PatientsPage = () => {
  const navigate = useNavigate();
  const access = useAccessCheck();

  const searchRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLButtonElement>(null);
  const tableRef = useRef<DataTableRef>(null);
  const gridRef = useRef<DataGridRef>(null);

  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isGrid, setIsGrid] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<PatientOverview | null>(null);
  const [roles, setRoles] = useState<any[]>([]);

  // Filter state
  const [genderFilter, setGenderFilter] = useState<string | null>(null);
  const [ageGroupFilter, setAgeGroupFilter] = useState<string | null>(null);
  const [appointmentFilter, setAppointmentFilter] = useState<string | null>(null);

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleSearch = () => {
    const query = searchRef.current?.value || "";
    setSearchQuery(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleClearFilters = () => {
    setGenderFilter(null);
    setAgeGroupFilter(null);
    setAppointmentFilter(null);
  };

  const activeFilterCount = [genderFilter, ageGroupFilter, appointmentFilter].filter(Boolean).length;

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
    }),
    [access],
  );

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      ...(genderFilter && { gender: genderFilter }),
      ...(ageGroupFilter && { age_group: ageGroupFilter }),
      ...(appointmentFilter && { has_appointment: appointmentFilter }),
    }),
    [searchQuery, genderFilter, ageGroupFilter, appointmentFilter],
  );

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        user_fullname: {
          name: "Patient Name",
          maxWidth: "250px",
        },
        user_email: {
          name: "Email",
          maxWidth: "250px",
        },
        user_phone_number: {
          name: "Phone",
          maxWidth: "150px",
        },
        age_gender: {
          name: "Age & Gender",
          maxWidth: "150px",
        },
        last_appointment_formatted: {
          name: "Last Appointment",
          maxWidth: "200px",
        },
      },
      dataSource: "/user-management/users/patients",
      primaryKey: "id",
      softDelete: true,
      displayInModal: true,
      noRecordText: "No Patients found",
    }),
    [],
  );

  const handleEditUser = async (user: PatientOverview) => {
    try {
      const response = await apiClient.get(`/user-management/users/patient/${user.id}`);
      const userData = response.data?.data || response.data;

      let roleId = "";
      if (userData.user_role) {
        if (typeof userData.user_role === "string") {
          try {
            roleId = JSON.parse(userData.user_role).id?.toString() || "";
          } catch {
            roleId = roles.find((r) => r.name === userData.user_role)?.id.toString() || "";
          }
        } else if (userData.user_role.id) {
          roleId = userData.user_role.id.toString();
        }
      } else if (userData.role_id) {
        roleId = userData.role_id.toString();
      }

      const userToEdit: PatientOverview = {
        ...userData,
        user_role: roleId,
      };
      setEditingUser(userToEdit);
      setIsDrawerOpen(true);
    } catch (error: any) {
      console.error(error);
    }
  };

  const gridOptions: DataGridOptions = useMemo(
    () => ({
      dataSource: "/user-management/users/patients",
      primaryKey: "id",
      itemsPerPage: 15,
      noRecordText: "No Patients found",
      renderCard: (patient: PatientOverview) => (
        <PatientsCardView key={patient.id} pat={patient} isListView={false} />
      ),
    }),
    [],
  );

  const refreshPatients = () => {
    if (isGrid) {
      gridRef.current?.reload();
    } else {
      tableRef.current?.reload();
    }
  };

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={{ base: 3, md: 5 }}
      >
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0} h="32px">
          <Flex shrink={0} direction="column" flex="1" minH={0} overflow="hidden">
            {/* Header Section */}
            <Box flexShrink={0}>
              <Flex
                direction={{ base: "column", lg: "row" }}
                justify="space-between"
                align={{ base: "flex-start", lg: "center" }}
                gap={{ base: 3, lg: 0 }}
              >
                <Box>
                  <Badge
                    className="rounded-sm border-[#2E37A4] border"
                    py="4px"
                    px="2px"
                    fontSize={{ base: "2xs", lg: "xs" }}
                    colorScheme="purple"
                  >
                    TOTAL PATIENTS: {total}
                  </Badge>
                </Box>
                <Flex
                  py={1}
                  pt={2}
                  pb={2}
                  shrink={0}
                  justify="space-between"
                  direction={{ base: "column", lg: "row" }}
                >
                  <HStack
                    spacing={3}
                    flexWrap="wrap"
                    h="32px"
                    w="100%"
                    justify={{ base: "space-between", sm: "flex-start" }}
                  >
                    {/* Search */}
                    <Box
                      flex="0 1 auto"
                      minW={{ base: "120px", sm: "150px", md: "180px" }}
                      maxW={{ base: "180px", md: "200px" }}
                      h="32px"
                      bg="white"
                      order={{ base: 1, sm: 1 }}
                    >
                      <InputGroup size="lg" h="full">
                        <InputLeftElement pointerEvents="none" h="full">
                          <Search size={16} color="gray" />
                        </InputLeftElement>
                        <Input
                          ref={searchRef}
                          placeholder="Search patients..."
                          defaultValue={searchQuery}
                          onChange={handleSearch}
                          onKeyDown={handleKeyDown}
                          fontSize={{ base: "sm", lg: "md" }}
                          h="full"
                        />
                      </InputGroup>
                    </Box>

                    {/* Filter Button */}
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        variant="outline"
                        size="sm"
                        h="32px"
                        bg="white"
                        fontSize={{ base: "xs", lg: "sm" }}
                        leftIcon={<Filter size={16} />}
                        order={{ base: 2, sm: 2 }}
                        isDisabled={!permissions.can_view}
                      >
                        Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                      </MenuButton>
                      <MenuList minW="200px" zIndex={1400}>

                        {/* Gender */}
                        <MenuGroup title="Gender" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3} mt={1}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!genderFilter ? "semibold" : "normal"}
                            color={!genderFilter ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={genderFilter === "Male" ? "semibold" : "normal"}
                            color={genderFilter === "Male" ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter("Male")}
                          >
                            Male
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={genderFilter === "Female" ? "semibold" : "normal"}
                            color={genderFilter === "Female" ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter("Female")}
                          >
                            Female
                          </MenuItem>
                        </MenuGroup>

                        <MenuDivider />

                        {/* Age Group */}
                        <MenuGroup title="Age Group" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!ageGroupFilter ? "semibold" : "normal"}
                            color={!ageGroupFilter ? "blue.600" : "inherit"}
                            onClick={() => setAgeGroupFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={ageGroupFilter === "child" ? "semibold" : "normal"}
                            color={ageGroupFilter === "child" ? "blue.600" : "inherit"}
                            onClick={() => setAgeGroupFilter("child")}
                          >
                            Child (0–12)
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={ageGroupFilter === "adult" ? "semibold" : "normal"}
                            color={ageGroupFilter === "adult" ? "blue.600" : "inherit"}
                            onClick={() => setAgeGroupFilter("adult")}
                          >
                            Adult (13–59)
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={ageGroupFilter === "senior" ? "semibold" : "normal"}
                            color={ageGroupFilter === "senior" ? "blue.600" : "inherit"}
                            onClick={() => setAgeGroupFilter("senior")}
                          >
                            Senior (60+)
                          </MenuItem>
                        </MenuGroup>

                        <MenuDivider />

                        {/* Appointment */}
                        <MenuGroup title="Appointment" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!appointmentFilter ? "semibold" : "normal"}
                            color={!appointmentFilter ? "blue.600" : "inherit"}
                            onClick={() => setAppointmentFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={appointmentFilter === "1" ? "semibold" : "normal"}
                            color={appointmentFilter === "1" ? "blue.600" : "inherit"}
                            onClick={() => setAppointmentFilter("1")}
                          >
                            Has Appointment
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={appointmentFilter === "0" ? "semibold" : "normal"}
                            color={appointmentFilter === "0" ? "blue.600" : "inherit"}
                            onClick={() => setAppointmentFilter("0")}
                          >
                            No Appointment
                          </MenuItem>
                        </MenuGroup>

                        {/* Clear Filters */}
                        {activeFilterCount > 0 && (
                          <>
                            <MenuDivider />
                            <MenuItem fontSize="sm" color="red.500" onClick={handleClearFilters}>
                              Clear All Filters
                            </MenuItem>
                          </>
                        )}
                      </MenuList>
                    </Menu>

                    {/* List / Grid Toggle */}
                    <Tabs variant="unstyled" h="32px" order={{ base: 3, sm: 3 }}>
                      <TabList className="rounded-md border flex items-center" h="32px">
                        <Tab
                          className="w-8 h-8"
                          onClick={() => setIsGrid(0)}
                          p={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box className={`p-1 rounded-sm ${!isGrid ? "bg-gray-400/20" : "bg-white"}`}>
                            <List size={16} />
                          </Box>
                        </Tab>
                        <Tab
                          className="w-8 h-8"
                          onClick={() => setIsGrid(1)}
                          p={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box className={`p-1 rounded-sm text-black ${isGrid ? "bg-gray-400/20" : "bg-white "}`}>
                            <LayoutGrid size={16} />
                          </Box>
                        </Tab>
                      </TabList>
                    </Tabs>

                    {permissions.can_create && (
                      <>
                        {/* Mobile: Icon only button */}
                        <IconButton
                          ref={drawerRef}
                          aria-label="New Patient"
                          icon={<Plus size={16} />}
                          bg="red.500"
                          _hover={{ bg: "#bf3c1fff" }}
                          color="white"
                          size="sm"
                          h="32px"
                          w="32px"
                          onClick={() => setIsDrawerOpen(true)}
                          display={{ base: "flex", sm: "none" }}
                          order={{ base: 4, sm: 5 }}
                          minW="32px"
                        />
                        {/* Desktop: Full button with text */}
                        <Button
                          ref={drawerRef}
                          py="6px"
                          px="10px"
                          bg="red.500"
                          _hover={{ bg: "#bf3c1fff" }}
                          colorScheme="blue"
                          fontSize={{ base: "xs", lg: "sm" }}
                          leftIcon={<Plus size={16} />}
                          h="32px"
                          onClick={() => setIsDrawerOpen(true)}
                          display={{ base: "none", sm: "flex" }}
                          order={{ base: 5, sm: 5 }}
                          flexShrink={0}
                          whiteSpace="nowrap"
                        >
                          New Patient
                        </Button>
                      </>
                    )}
                  </HStack>
                </Flex>
              </Flex>
            </Box>

            {/* Content Section - Grid or Table */}
            {isGrid ? (
              <DataGrid
                ref={gridRef}
                options={gridOptions}
                params={tableParams}
                setTotal={setTotal}
                gridColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(1, 1fr)",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(2, 1fr)",
                  xl: "repeat(3, 1fr)",
                  "2xl": "repeat(3, 1fr)",
                }}
              />
            ) : (
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                setTotal={setTotal}
                showCheckbox={false}
                onOpenModal={handleEditUser}
                onOpenItem={(id) => navigate(`/patientprofile/${id}`)}
              />
            )}
          </Flex>
        </VStack>
      </Box>

      <AddPatientModal
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        btnRef={drawerRef}
        setPatientsData={() => {}}
        refreshPatients={refreshPatients}
        editingUser={editingUser}
      />
    </AdminLayout>
  );
};

export default PatientsPage;